import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { Country, EINData, EntityType, ExtEntitySaveRequest, ExtEntitySaveResponse, FilterParams, HomeOfficeEntity, SSNEINCheck, State,  } from './models/ext-entity.interface';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ExtEntityMaintenanceService {
  private baseURLs: GtwElementsBaseUrlsModel = { 'admin': '', 'api': '', "custom": '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };
  private globalParams: any;
  private filterParams: FilterParams | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Initialize service with base URLs and global parameters
   */
  initialize(baseURLs: GtwElementsBaseUrlsModel, globalParams: any, filterParams: FilterParams): void {
    console.log('Initializing service with:', {
      baseURLs: baseURLs,
      globalParams: globalParams,
      filterParams: filterParams
    });

    this.baseURLs = baseURLs;
    this.globalParams = globalParams;
    this.filterParams = filterParams;

    // Validate critical configuration
    if (!baseURLs?.api) {
      console.error('Service initialization warning: API base URL is missing');
    }
    
    if (!filterParams?.tax_year) {
      console.error('Service initialization warning: Tax year is missing from filter params');
    }

    if (!globalParams) {
      console.error('Service initialization warning: Global params is missing');
    }

    console.log('Service initialized successfully');
  }

  /**
   * Save external entity setup - equivalent to saveExternalEntitySetup from AngularJS service
   */
     saveExternalEntitySetup(
     settings: any,
     globalService:  any,
     filterParams: FilterParams,
     extSetupSaveDtls: ExtEntitySaveRequest
   ): Observable<ExtEntitySaveResponse> {
     const url = `${this.baseURLs.api}/saveJsonObject?action_code=icldo6`;

     // 🚀 CRITICAL V5: Build AngularJS-compatible payload structure (F7004 pattern)
     let params = _.merge({}, globalService?.globalParams || {}, filterParams);
     
     const jsonObj = JSON.stringify(extSetupSaveDtls);
     const jsonSettings = JSON.stringify(settings);

     params = _.extend({ clob_settings: jsonSettings }, params);
     params = _.extend({ clob_data: jsonObj }, params);
     
     if (!this.filterParams) {
       throw new Error('Service not initialized. Call initialize() first.');
     }
     
     // Add all required parameters as per the expected format
     params.is_issue_key_enabled = 'N';
     params.is_locked = 'N';
     params.issue_id = '';
     params.issue_id_for_tx_save = '';
     params.issue_short_desc = '';
     params.scenarioCode = 'RAF';
     params.process_name = 'External Entity Setup';
     params.screen_type = 'grid';
     
     // Ensure these parameters are set from the correct sources
     params.tax_year = filterParams?.tax_year || settings?.tax_year;
     params.scenario = filterParams?.scenario || settings?.scenario;
     params.jcd_key = globalService?.globalParams?.jcd_key || filterParams?.jcd_key || settings?.jcd_key;
     params.jcd_ta_key = globalService?.globalParams?.jcd_ta_key || filterParams?.jcd_ta_key;
     params.screen_key = globalService?.globalParams?.screen_key || filterParams?.screen_key;
     params.sso_id = settings?.sso_id || globalService?.globalParams?.sso_id;
     params.activity_key = filterParams?.activity_key || globalService?.globalParams?.activity_key; 
    // const requestData = {
    //   ...params,
    //   clob_data: jsonObj,
    //   clob_settings: jsonSettings,
    //   tax_year: this.filterParams.tax_year,
    //   scenario: this.filterParams.scenario,
    //   jcd_key: this.globalParams.jcd_key,
    //   process_name: 'External Entity Setup',
    //   sso_id: settings.sso_id,
    //   scenarioCode: 'RAF',
    //   is_issue_key_enabled : 'N'
    // };

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        urlEncoded.set(key, params[key]);
      }
    });

    return this.http.post<ExtEntitySaveResponse>(url, urlEncoded.toString(),  httpOptions)
      .pipe(
        map(response => response),
        catchError(this.handleError)
      );
  }

  /**
   * Load all entity types - equivalent to loadAllEntityTypes
   */
  loadAllEntityTypes(paramData : any): Observable<any[]> {
    // Validate required parameters
    if (!paramData?.taxYear || !paramData?.scenario || !paramData?.jcd_key || !paramData?.sso_id) {
      return throwError(new Error('Missing required parameters'));
    }

    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'pn93eo',
      tax_year: paramData.taxYear,
      scenario: paramData.scenario,
      jcd_key: paramData.jcd_key,
      sso_id: paramData.sso_id,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: paramData.jcd_ta_key || '',
      screen_key: paramData.screen_key || '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any }>(url, { params })
      .pipe(
        map(response => response.jsonObject),
        catchError(this.handleError)
      );
  }

  /**
   * Load country list - equivalent to loadCountryList
   */
  loadCountryList(paramData : any): Observable<any[]> {
    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'ynqodb',
      tax_year: paramData.taxYear,
      scenario: paramData.scenario,
      jcd_key: paramData.jcd_key,
      sso_id: paramData.sso_id,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: paramData.jcd_ta_key || '',
      screen_key: paramData.screen_key || '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any }>(url, { params })
      .pipe(
        map(response => response.jsonObject),
        catchError(this.handleError)
      );
  }

  /**
   * Load state list - equivalent to loadStateList
   */
  loadStateList(paramData : any): Observable<any[]> {
    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'gfze8m',
      tax_year: paramData.taxYear,
      scenario: paramData.scenario,
      jcd_key: paramData.jcd_key,
      sso_id: paramData.sso_id,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: paramData.jcd_ta_key || '',
      screen_key: paramData.screen_key || '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any }>(url, { params })
      .pipe(
        map(response => response.jsonObject),
        catchError(this.handleError)
      );
  }

  /**
   * Load home office entity list - equivalent to loadHoleList
   */
  loadHomeOfficeList(paramData : any): Observable<any[]> {
    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'oxfnru',
      tax_year: paramData.taxYear,
      scenario: paramData.scenario,
      jcd_key: paramData.jcd_key,
      sso_id: paramData.sso_id,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: paramData.jcd_ta_key || '',
      screen_key: paramData.screen_key || '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any[] }>(url, { params })
      .pipe(
        tap(response => {
        }),
        map(response => {
          const validatedData = this.validateHomeOfficeData(response.jsonObject);
          return validatedData;
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  /**
   * Type guard for HomeOfficeEntity validation
   */
  private isHomeOfficeEntity(item: any): item is HomeOfficeEntity {
    return item && 
           (typeof item.HO_CODE === 'string' || typeof item.HO_CODE === 'number') &&
           typeof item.HO_DESC === 'string';
  }

  /**
   * Validate and transform HomeOfficeEntity data
   */
  private validateHomeOfficeData(data: any[]): HomeOfficeEntity[] {

    if (!data) {
      return [];
    }

    if (!Array.isArray(data)) {
      return [];
    }

    if (data.length === 0) {
      return [];
    }

    const validData = data.filter(item => {
      const isValid = this.isHomeOfficeEntity(item);
      return isValid;
    });


    const result = validData.map(item => ({
      HO_CODE: item.HO_CODE,
      HO_DESC: item.HO_DESC
    }));
    return result;
  }

  /**
   * Set Home Office EIN - equivalent to setHO_EIN
   */
  setHomeOfficeEIN(taxYear: string, scenario: string, jcdKey: string, le: string): Observable<any[]> {
    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'zg806r',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      le: le,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: '',
      screen_key: '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any }>(url, { params })
      .pipe(
        map(response => response.jsonObject),
        catchError(this.handleError)
      );
  }

  /**
   * Check if SSN/EIN already exists - equivalent to SSN/EIN existence check
   */
  checkSSNEINExists(taxYear: string, scenario: string, jcdKey: string, ssnEin: string): Observable<any[]> {
    const url = `${this.baseURLs.api}/loadJsonObject`;
    const params = {
      action_code: 'uaw9ux',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKey,
      ssn_ein: ssnEin,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_ta_key: '',
      screen_key: '',
      screen_type: 'grid'
    };

    return this.http.get<{ jsonObject: any }>(url, { params })
      .pipe(
        map(response => response.jsonObject),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(error);
  }
}